import constate from 'constate'
import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import {
  PaginatedSearchState,
  PaginatedSearchStore,
  usePaginatedSearch,
} from 'scenes/Dashboard/components-next/PaginatedSearch'
import { deleteEntityFromCache, deleteEntityFromListCache } from 'utils-next/api'
import { PaginationOptions } from 'utils-next/types'

import { showDeleteUserModal } from '../../modals'
import { failedDeleteAlertAction, successfulDeleteAlertAction } from './UserListViewAlerts.component'
import { getApiUserHandlers, User, USER_CACHE_STORE_ID, UserListFilters } from '../../../../../../entities-next/user'

export type UserListViewStore = {
  state: PaginatedSearchStore<User, UserListViewFilters>['state']
  actions: PaginatedSearchStore<User, UserListViewFilters>['actions'] & {
    deleteUser: (user: User) => Promise<void>
  }
}

export type UserListViewFilters = UserListFilters

function useUserListViewInternalStore(): UserListViewStore {
  const { state, actions: paginatedSearchActions } = usePaginatedSearch<User, UserListViewFilters>(
    USER_CACHE_STORE_ID,
    userListQuery,
    {
      initialFilters: {},
    }
  )
  const userListViewActions = useUserListViewActions(state)
  return {
    state,
    actions: {
      ...paginatedSearchActions,
      ...userListViewActions,
    },
  }
}

function useUserListViewActions(state: PaginatedSearchState<User, UserListViewFilters>) {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const deleteUser = useCallback(
    async (user: User) => {
      const { apiDeleteUser } = getApiUserHandlers()
      const shouldDelete = await showDeleteUserModal({ corporate: user })
      if (shouldDelete) {
        const queryKey = [
          USER_CACHE_STORE_ID,
          { ...state.filters },
          {
            currentPage: state.currentPage,
            limit: state.limit,
          },
        ]
        apiDeleteUser(user.id)
          .then(() => {
            deleteEntityFromListCache(queryClient, user.id, queryKey)
            deleteEntityFromCache(queryClient, user.id, USER_CACHE_STORE_ID)
            dispatch(successfulDeleteAlertAction(user))
          })
          .catch(() => {
            dispatch(failedDeleteAlertAction(user))
          })
      }
    },
    [dispatch]
  )

  return {
    deleteUser,
  }
}

async function userListQuery(_key: string, filters: UserListViewFilters, paginationOptions: PaginationOptions) {
  const { apiGetUserList } = getApiUserHandlers()
  const userList = await apiGetUserList(filters, paginationOptions)
  return userList.results
}

export const [UserListViewProvider, useUserListViewStore] = constate(useUserListViewInternalStore)
